/**
 * Two clocks, and every instant labelled with the one it is on.
 *
 * §7.5 is one rule applied twice: show the clock the reader is reasoning in.
 *
 * - **A platform record** — a tenant's Created, a key's Created — is rendered in the
 *   **platform owner's own** zone. The owner reasoning about *when they retired a tenant*
 *   is reasoning in their own day; rendering two of them in two different tenants' zones
 *   would make them look out of order.
 * - **A tenant's own record** — when a superadmin's link expires — is rendered in **that
 *   tenant's** configured zone. The owner reading a link's expiry is about to say it out
 *   loud to somebody standing in that tenant's building, and "this works until 17:40, your
 *   time" is the only useful form of that sentence.
 *
 * **Every instant is labelled with the zone it is in**, always, because this surface mixes
 * two. A bare time here is exactly the ambiguity `docs/reference/ISSUES.md` **CF5** is
 * about.
 *
 * **The language is fixed; the zone is not** (TAS-12). Every label on this console is English, so
 * every date and time is too — whatever the owner's browser is set to. The browser's language
 * used to leak in (`1 січ. 2026 р.` under a sentence of English), and the surface read as two
 * products. The zone logic above is untouched: this decides how a clock is *written*, never
 * which clock is read.
 */

/**
 * The locale of every date and time on the console, and the only one.
 *
 * `en-GB` rather than `en`: both are English, but `en` is the United States' — month first, a
 * 12-hour clock with AM and PM — and the rest of the product writes times on a 24-hour clock
 * (docs/ux/DESIGN-SYSTEM.md: "times are 24-hour everywhere"). `en-GB` reads `15 Aug 2026,
 * 14:30`: the month is a word, so the order cannot be misread, and `14:30` cannot be taken for
 * the morning. It is a constant rather than `document.documentElement.lang` because nothing
 * about this console is localised, and a value read from the page is a value that can drift.
 */
export const CONSOLE_LOCALE = 'en-GB'

/**
 * The zone this browser is in — the platform owner's own day.
 *
 * Built without a locale on purpose: this asks which clock the machine is on and formats
 * nothing, so there is no language for it to leak. (It is also the one question
 * `testing/overview.tsx`'s `stubOwnerTimeZone` answers differently, by its argument list.)
 */
export function ownerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * An instant, formatted in a zone, **without** the zone named.
 *
 * For a place that names the zone itself, beside the time and in its own element — the
 * tenant header sets the IANA name in monospace after the time, where `formatInstant`'s
 * parenthesised label would say it twice. Every other caller wants {@link formatInstant}: a
 * bare time on this surface is the ambiguity **CF5** is about, so this one is only for a
 * caller that prints the label next to it.
 *
 * `Intl` does the formatting, in {@link CONSOLE_LOCALE}. An unparseable instant is a bug
 * somewhere upstream, and it must read as a gap rather than as
 * `Invalid Date` — which is what a screen renders if nobody checks.
 */
export function formatInstantWithoutZone(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso === '') {
    return '—'
  }
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) {
    return '—'
  }

  return new Intl.DateTimeFormat(CONSOLE_LOCALE, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(value)
}

/**
 * An instant, formatted in a zone, with the zone named.
 *
 * The zone is appended as its IANA name rather than as an abbreviation: `CET` is ambiguous
 * twice a year, and the console shows the unabbreviated name everywhere else too (§7.1).
 */
export function formatInstant(iso: string | null | undefined, timeZone: string): string {
  const formatted = formatInstantWithoutZone(iso, timeZone)
  return formatted === '—' ? formatted : `${formatted} (${timeZone})`
}

/** A date with no time, in the owner's zone — the tenant list's Created column. */
export function formatDate(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso === '') {
    return '—'
  }
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat(CONSOLE_LOCALE, { dateStyle: 'medium', timeZone }).format(value)
}
